/**
 * Generic fuzzy matching utility for filtering select filter options client-side.
 *
 * ## Normalization
 * Both the query and candidate text are normalized before comparison by:
 *   - Converting to lowercase
 *   - Stripping all non-alphanumeric characters (hyphens, underscores, spaces, punctuation, etc.)
 *
 * This means "trex" will match "t-rex", "t_rex", "T.Rex", "(T)Rex", etc.
 *
 * ## Matching strategy (applied to normalized strings)
 * 1. **Substring match** — normalized candidate contains normalized query as a contiguous substring.
 *    e.g. "flu" matches "confluence" → "confluence"
 * 2. **Subsequence match** — every character in the normalized query appears in the normalized
 *    candidate in the same relative order (but not necessarily contiguous).
 *    e.g. "cnf" matches "confluence" → "cnflue**n**ce" (c…n…f)
 *
 * An empty query matches everything.
 */

/** Strip all non-alphanumeric characters and lowercase. */
const normalize = (text: string): string => text.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Returns true if every character in `query` appears in `text` in the same
 * relative order (subsequence match).
 */
const isSubsequence = (text: string, query: string): boolean => {
	let queryIndex = 0;
	for (let i = 0; i < text.length && queryIndex < query.length; i++) {
		if (text[i] === query[queryIndex]) {
			queryIndex++;
		}
	}
	return queryIndex === query.length;
};

/**
 * Tests whether a single candidate text fuzzy-matches the query.
 *
 * Exported for use in unit tests and one-off checks. Most callers should
 * prefer `fuzzyFilter` which handles the array iteration.
 *
 * @param query     - The search string typed by the user.
 * @param candidate - The text to test against (e.g. an option label).
 * @returns true if the candidate matches the query.
 */
export const fuzzyMatch = (query: string, candidate: string): boolean => {
	const normalizedQuery = normalize(query);

	// Empty query matches everything
	if (!normalizedQuery) {
		return true;
	}

	const normalizedCandidate = normalize(candidate);

	// Nothing left after stripping non-alphanumeric — can't match a non-empty query
	if (!normalizedCandidate) {
		return false;
	}

	// 1. Substring match (faster — try first)
	if (normalizedCandidate.includes(normalizedQuery)) {
		return true;
	}

	// 2. Subsequence match
	return isSubsequence(normalizedCandidate, normalizedQuery);
};

/**
 * Filters an array of items by fuzzy-matching the query against a text accessor.
 *
 * Drop-in replacement for simple `.filter()` calls in lookup fetchers.
 *
 * @example
 * // In lookupChannels:
 * return fuzzyFilter(query, allOptions, (option) => option.label);
 *
 * @param query   - The search string typed by the user.
 * @param items   - The array of items to filter.
 * @param getText - A function that extracts the text to match against from each item.
 * @returns A filtered array containing only items that fuzzy-match the query.
 */
export const fuzzyFilter = <T>(query: string, items: T[], getText: (item: T) => string): T[] => {
	if (!query) {
		return items;
	}
	return items.filter((item) => fuzzyMatch(query, getText(item)));
};
